/**
 * FirmUserManagementPage — Claims Admin manages firm users and roles.
 * Blueprint: F-14 — new UI component, Claims Admin access only.
 *
 * Features: list firm users with role badges, invite by email,
 *           promote CLIENT_USER → CLAIMS_ADMIN, deactivate users.
 * Constraints: Claims Admin cannot promote to REVIEWER or SYSTEM_ADMIN (F-14).
 *
 * TODO: GET /firms/:id/users — list all firm users.
 * TODO: POST /firms/:id/invite — send 48-hour token email.
 * TODO: PUT /firms/:id/users/:uid/role — promote/demote.
 * TODO: DELETE /firms/:id/users/:uid — deactivate (sets is_active=false).
 */
import React, { useState } from 'react';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { UserPlus, Shield, User } from 'lucide-react';
import type { FirmUser } from '@/types/cmm/claimant.types';

const BREADCRUMBS = [{ label: 'Admin' }, { label: 'Firm Users' }];

const FirmUserManagementPage: React.FC = () => {
  const [users,       setUsers]       = useState<FirmUser[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole,  setInviteRole]  = useState<'CLIENT_USER' | 'CLAIMS_ADMIN'>('CLIENT_USER');
  const [inviting,    setInviting]    = useState(false);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      // TODO: POST /firms/:firm_id/invite — { email: inviteEmail, role: inviteRole }
      //   Response: 202 Accepted, 48-hour token email sent
      console.warn('[FirmUserManagementPage] invite — TODO:', inviteEmail, inviteRole);
      setInviteEmail('');
    } finally {
      setInviting(false);
    }
  };

  const ROLE_STYLES: Record<string, string> = {
    CLIENT_USER:  'bg-gray-100 text-gray-700',
    CLAIMS_ADMIN: 'bg-blue-100 text-blue-700',
  };

  return (
    <BaseLayout title="Firm Users" subtitle="Manage team members and their access roles"
      breadcrumbs={BREADCRUMBS}>
      <div className="grid grid-cols-3 gap-6">
        {/* User List */}
        <div className="col-span-2 bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Team Members</h3>
          </div>
          {users.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              No users yet — TODO: load from GET /firms/:id/users
            </div>
          ) : users.map(user => (
            <div key={user.id} className="flex items-center gap-4 px-5 py-3 border-b border-gray-100">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <User className="w-4 h-4 text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">{user.name}</p>
                <p className="text-xs text-gray-400">{user.email}</p>
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ROLE_STYLES[user.role]}`}>
                {user.role}
              </span>
              <div className="flex gap-2 text-xs">
                <button className="text-blue-600 hover:underline">Promote</button>
                <button className="text-red-500 hover:underline">Deactivate</button>
              </div>
            </div>
          ))}
        </div>

        {/* Invite Form */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Invite Team Member
          </h3>
          <form onSubmit={handleInvite} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email Address</label>
              <input type="email" required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                placeholder="attorney@lawfirm.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={inviteRole} onChange={e => setInviteRole(e.target.value as any)}>
                <option value="CLIENT_USER">Client User</option>
                <option value="CLAIMS_ADMIN">Claims Admin</option>
                {/* Note: Cannot assign REVIEWER or SYSTEM_ADMIN here — F-14 */}
              </select>
            </div>
            <button type="submit" disabled={inviting}
              className="w-full py-2 text-sm font-medium text-white bg-blue-600 rounded-md disabled:opacity-50">
              {inviting ? 'Sending...' : 'Send Invitation'}
            </button>
            <p className="text-xs text-gray-400 text-center">48-hour invite link emailed to recipient</p>
          </form>
        </div>
      </div>
    </BaseLayout>
  );
};

export default FirmUserManagementPage;
